import { InteractionRequiredAuthError, InteractionStatus, InteractionType } from '@azure/msal-browser'
import { useMsal, useMsalAuthentication } from '@azure/msal-react'
import { getAuthorityForAccount, getLoginRequest, protectedResources } from '@src/authConfig'
import PageSpinner from '@src/components/PageSpinner'
import { AppContext, useAppStore } from '@src/contexts/AppContext'
import { isExpiredToken, parseJwt } from '@src/data/api/api-client'
import useFeatureFlags from '@src/hooks/config-hooks'; // #62287
import { clearUserToken, saveUserToken, signIn } from '@src/services/Authentification'
import { reportErrorToConsole } from '@src/services/error-logger'
import { UserDtoFromJwt } from '@src/types/User'
import { isAxiosError } from 'axios'
import { useEffect, useRef, type JSX } from 'react'

interface Props {
  children: JSX.Element
}

/**
 * Hosts MSAL's automatic login / silent token acquisition (#66555).
 * The hook lives in its own component so it can be unmounted while a merchant
 * switch is pending: the hook is one-shot and its acquireTokenSilent call fails
 * once the switch clears the cache, falling back to its own loginRedirect. That
 * second concurrent B2C transaction evicts the first one from B2C's transaction
 * cookies (x-ms-cpim-trans), so Entra's reply can no longer be correlated and
 * /oauth2/authresp fails with a bare 400.
 */
const MsalAutoLogin = ({ flags }: { flags: Record<string, boolean> }) => {
  useMsalAuthentication(InteractionType.Redirect, getLoginRequest(flags))
  return null
}

const UserProvider = ({ children }: Props) => {
  const { instance, accounts, inProgress } = useMsal()
  const { flags } = useFeatureFlags() // #62287
  const appStore = useAppStore()
  const switchInitiatedRef = useRef(false)

  // #62287 — switch merchant detection: the URL carries a targetMerchantId and either
  // the FS is off (kill-switch -> standard policy), the cached MSAL account belongs to
  // another merchant, or there is no account yet. The merchant is read from
  // extension_MerchantsList in the cached token claims, not from appStore.user, which
  // is still null on the first mount after navigation.
  const targetMerchantId = new URLSearchParams(window.location.search).get('targetMerchantId')
  const cachedClaims = accounts[0]?.idTokenClaims as { extension_MerchantsList?: string } | undefined
  const cachedMerchantId = cachedClaims?.extension_MerchantsList
  const nonMatchingMerchantId = !!cachedMerchantId && cachedMerchantId !== targetMerchantId
  const interactiveLoginRequired =
    !!targetMerchantId &&
    (!accounts[0] || !flags.EnableMerchantDashboardAccessFromMerchants || nonMatchingMerchantId)

  useEffect(() => {
    if (interactiveLoginRequired) {
      // Fire at most once, and never while MSAL is mid-interaction: every concurrent
      // loginRedirect opens a new B2C transaction and the newest one evicts the others
      // from x-ms-cpim-trans, so Entra's reply can no longer be correlated (#66555).
      if (inProgress === InteractionStatus.None && !switchInitiatedRef.current) {
        switchInitiatedRef.current = true
        const switchMerchant = async () => {
          clearUserToken()
          if (accounts[0]) await instance.clearCache()
          await instance.loginRedirect(getLoginRequest(flags))
        }
        switchMerchant().catch((error) => {
          // Re-arm so the next state change can retry instead of leaving a blank app
          switchInitiatedRef.current = false
          reportErrorToConsole(error)
        })
      }
      return
    }

    if (accounts[0] && appStore.user === null && inProgress === InteractionStatus.None) {
      instance.setActiveAccount(accounts[0])
      instance
        .acquireTokenSilent({
          account: accounts[0],
          scopes: protectedResources.getTokenAzure.scopes.read,
          authority: getAuthorityForAccount(accounts[0]),
        })
        .then((response) => {
          return signIn({ jwt: response.idToken }).then(async (r) => {
            const icebergToken = parseJwt(r.jwt) as UserDtoFromJwt
            if (isExpiredToken(icebergToken.exp)) {
              await instance.loginRedirect(getLoginRequest(flags))
              return
            }
            if (!icebergToken.merchantId) {
              await instance.loginRedirect({ ...getLoginRequest(flags), prompt: 'login' })
              return
            }
            saveUserToken(r.jwt)
            appStore.reloadUser()
          })
        })
        .catch((error) => {
          // #65781 — access revoked (disabled/deleted) during the silent re-mint: the exchange
          // returns 403. Clear the session and do a full MSAL logout instead of looping back into
          // acquireTokenSilent (which would otherwise re-mint from the cached account).
          if (isAxiosError(error) && error.response?.status === 403) {
            clearUserToken()
            instance.logoutRedirect({ account: accounts[0] }).catch(reportErrorToConsole)
            return
          }
          if (error instanceof InteractionRequiredAuthError) {
            reportErrorToConsole(new Error('Failed to acquire token silently. Redirecting to login.'))
            instance.loginRedirect(getLoginRequest(flags)).catch(reportErrorToConsole)
          } else {
            reportErrorToConsole(error)
          }
        })
    }
  }, [inProgress, accounts, instance, flags, appStore, interactiveLoginRequired])

  // While the switch login is pending, render only a spinner: children would query the previous merchant
  // with a stale token (visible 401s that re-trigger this effect), and MsalAutoLogin
  // could start a competing B2C transaction.
  if (interactiveLoginRequired) return <PageSpinner isLoading withBackdrop />

  return (
    <>
      <MsalAutoLogin flags={flags} />
      {inProgress === InteractionStatus.None && accounts.length > 0 && appStore.user !== null ? (
        <AppContext.Provider value={appStore}>{children}</AppContext.Provider>
      ) : (
        // Post-redirect wait: MSAL is processing the B2C response and the Iceberg JWT
        // exchange has not completed yet
        <PageSpinner isLoading withBackdrop />
      )}
    </>
  )
}

export default UserProvider
